import { z } from "zod";
import { disqualificationReasonSchema } from "./conversions";

/** "Archivia come perso / come non qualificato": why the lead is closed (ADR 0070). */
export const LEAD_CLOSE_STAGES = ["lost", "unqualified"] as const;
export const leadCloseStageSchema = z.enum(LEAD_CLOSE_STAGES);
export type LeadCloseStage = z.infer<typeof leadCloseStageSchema>;

/** Body of `DELETE /leads/:id`, optional: without `closeAs` the lead keeps its stage. */
export const leadArchiveInputSchema = z
  .object({
    closeAs: leadCloseStageSchema.optional(),
    disqualificationReason: disqualificationReasonSchema.optional(),
    disqualificationNote: z.string().trim().max(1000).optional(),
  })
  .strict();
export type LeadArchiveInput = z.infer<typeof leadArchiveInputSchema>;

/** Body of `POST /leads/:id/restore`, optional: `undo` is "Annulla" right after archiving. */
export const leadRestoreInputSchema = z.object({ undo: z.boolean().optional() }).strict();
export type LeadRestoreInput = z.infer<typeof leadRestoreInputSchema>;

/** Body of `POST /leads/:id/spam`: «Spam» archives the lead as spam, «Non è spam» brings it back (ADR 0093). */
export const leadSpamInputSchema = z.object({ spam: z.boolean() }).strict();
export type LeadSpamInput = z.infer<typeof leadSpamInputSchema>;

/**
 * Body of `POST /leads/:id/turn`, the switch "Tocca a me" in the chat (ADR 0083):
 * `false` takes the lead's last messages out (they do not count, follow-ups
 * too), `true` puts them back or marks the chat by hand.
 */
export const leadTurnInputSchema = z.object({ mine: z.boolean() }).strict();
export type LeadTurnInput = z.infer<typeof leadTurnInputSchema>;

/** At most this many leads in one "Archivia" or "Annulla" from the selection of Posta. */
export const LEAD_BATCH_MAX = 100;

const leadBatchIdsSchema = z
  .array(z.string().uuid())
  .min(1, "Seleziona almeno una conversazione.")
  .max(LEAD_BATCH_MAX, `Al massimo ${LEAD_BATCH_MAX} conversazioni alla volta.`)
  .refine((ids) => new Set(ids).size === ids.length, "Ogni conversazione una volta sola.");

/** Body of `POST /leads/archive`: the selection of Posta, archived as one by one. */
export const leadBatchArchiveInputSchema = z
  .object({
    leadIds: leadBatchIdsSchema,
    closeAs: leadCloseStageSchema.optional(),
    disqualificationReason: disqualificationReasonSchema.optional(),
    disqualificationNote: z.string().trim().max(1000).optional(),
  })
  .strict();
export type LeadBatchArchiveInput = z.infer<typeof leadBatchArchiveInputSchema>;

/** Body of `POST /leads/restore`: "Annulla" of a batch archive, or a batch "Ripristina". */
export const leadBatchRestoreInputSchema = z
  .object({ leadIds: leadBatchIdsSchema, undo: z.boolean().optional() })
  .strict();
export type LeadBatchRestoreInput = z.infer<typeof leadBatchRestoreInputSchema>;

/** What a batch did: the leads it changed, and why the others were left as they were. */
export type LeadBatchResultDto = {
  done: string[];
  failed: Array<{ leadId: string; code: string; message: string }>;
  /**
   * Archived "come perso" or "come non qualificato": the customers among `done`,
   * archived keeping their stage, as "Archivia" on one customer does.
   */
  customers?: string[];
};
